import React from 'react';
import { styled } from '@mui/material';
import { Dialog, DialogTitle } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import ErrorMessage from 'components/common/ErrorMessage';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import { useDispatch } from 'react-redux';
import { adminAddTravelTime } from 'slices/dashboardSlice';
import { interpreterAddTravelTime } from 'slices/interpreter/dashboardSlice';
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import { fetchInterpreterOptions } from 'slices/interpreterSlice';

const BootstrapDialog = styled(Dialog)(() => ({
  '& .MuiDialog-container': {
    overflowY: 'auto',
    margin: 'auto',
    alignItems: 'flex-start',
  },
  '& .MuiPaper-root': {
    maxHeight: 'none',
    overflowY: 'hidden',
    borderRadius: 20,
  },
}));

const AddTravelTimeModal = ({ show, onClose, onSuccess, isAdmin = true }) => {
  const dispatch = useDispatch();
  const [selectedInterpreter, setSelectedInterpreter] = React.useState(null);

  const { control, handleSubmit, reset, formState: { errors }, setError } = useForm({
    defaultValues: {
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      notes: '',
    },
  });

  const interpreterLoadOptions = async (inputValue, loadedOptions, { page }) => {
    return await dispatch(fetchInterpreterOptions(page, { pageLength: 20, sortBy: 'id', sortDirection: 'asc', search: inputValue }));
  };

  const onSubmit = (data) => {
    if (isAdmin && !selectedInterpreter) {
      setError('interpreter_id', { message: 'Please select an interpreter' });
      return;
    }
    const payload = isAdmin
      ? { ...data, interpreter_id: selectedInterpreter.value }
      : data;
    const action = isAdmin ? adminAddTravelTime : interpreterAddTravelTime;
    dispatch(action(payload, (success, message) => {
      if (success) {
        toast.success(message, { className: 'successToast' });
        reset();
        if (isAdmin) setSelectedInterpreter(null);
        onClose();
        if (onSuccess) onSuccess();
      } else {
        toast.error(message || 'Something went wrong', { className: 'errorToast' });
      }
    }));
  };

  const handleClose = () => {
    reset();
    if (isAdmin) setSelectedInterpreter(null);
    onClose();
  };

  return (
    <BootstrapDialog
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={show}
      sx={{
        '& .MuiDialog-container': { alignItems: 'center' },
        '& .MuiPaper-root': { overflowY: 'visible' },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-content full_modal">
          <DialogTitle>
            <div className="modal-header header_modal align-items-center">
              <div></div>
              <h5 className="modal-title_u">Add Travel Time</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={handleClose} />
            </div>
          </DialogTitle>

          <div className="modal-body modal_form_body">
            {isAdmin && (
              <div className="row-primary mt-0 mb-3">
                <h2>Interpreter *</h2>
                <div style={{ width: '100%' }}>
                  <AsyncPaginate
                    menuPlacement="auto"
                    styles={{ ...selectCustomStyles }}
                    value={selectedInterpreter}
                    loadOptions={interpreterLoadOptions}
                    isClearable
                    escapeClearsValue
                    placeholder="Select interpreter..."
                    onChange={(obj) => setSelectedInterpreter(obj || null)}
                    additional={{ page: 1 }}
                  />
                  {errors?.interpreter_id?.message && (
                    <ErrorMessage text={errors.interpreter_id.message} />
                  )}
                </div>
              </div>
            )}

            <div className="row-primary row-r">
              <div className="date">
                <h2>Start Date*</h2>
                <Controller
                  name="start_date"
                  control={control}
                  rules={{ required: 'Start date is required' }}
                  render={({ field }) => (
                    <div style={{ width: '100%' }}>
                      <div className="input-image">
                        <input type="date" {...field} placeholder="YYYY-MM-DD" />
                        <img src="/images/celender.png" alt="calendar icon" className="custom-icon" />
                      </div>
                      {errors?.start_date?.message && (
                        <ErrorMessage text={errors.start_date.message} />
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="date">
                <h2>End Date*</h2>
                <Controller
                  name="end_date"
                  control={control}
                  rules={{ required: 'End date is required' }}
                  render={({ field }) => (
                    <div style={{ width: '100%' }}>
                      <div className="input-image">
                        <input type="date" {...field} placeholder="YYYY-MM-DD" />
                        <img src="/images/celender.png" alt="calendar icon" className="custom-icon" />
                      </div>
                      {errors?.end_date?.message && (
                        <ErrorMessage text={errors.end_date.message} />
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="row-primary row-r mt-3">
              <div className="date">
                <h2>Start Time <span style={{ fontWeight: 400, fontSize: '13px' }}>(optional)</span></h2>
                <Controller
                  name="start_time"
                  control={control}
                  render={({ field }) => (
                    <div className="input-image">
                      <input type="time" {...field} />
                      <img src="/images/clock.png" alt="clock" className="custom-icon" />
                    </div>
                  )}
                />
              </div>
              <div className="date">
                <h2>End Time <span style={{ fontWeight: 400, fontSize: '13px' }}>(optional)</span></h2>
                <Controller
                  name="end_time"
                  control={control}
                  render={({ field }) => (
                    <div className="input-image">
                      <input type="time" {...field} />
                      <img src="/images/clock.png" alt="clock" className="custom-icon" />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="row-primary mt-3">
              <h2>Notes</h2>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    className="type_inp input-in"
                    placeholder="Enter notes (optional)"
                  />
                )}
              />
            </div>

            <div className="modal-body d-flex justify-content-end gap-2 mt-2">
              <button type="button" className="Cancle_button" onClick={handleClose}>
                Cancel
              </button>
              <SubmitButton buttonType="submit" btnText="Submit" />
            </div>
          </div>
        </div>
      </form>
    </BootstrapDialog>
  );
};

export default AddTravelTimeModal;
